import {i18n} from '../../i18n/i18n'

import React, {Component, Fragment, RefObject} from 'react'
import {connect, ConnectedProps} from 'react-redux'
import {RouteComponentProps, withRouter} from 'react-router-dom'
import {produce} from 'immer'
import {find, includes} from 'lodash'
import {
    Transformer as HTMLTransformerElement
} from 'konva/lib/shapes/Transformer'
import {
    replaceId,
    resetId,
    replaceMarkup,
    replaceMarkupTarget,
    replaceMarkupType,
    replaceMarkupContactId,
    replaceMarkupItemId,
    replaceMarkupCategoryId,
    replaceMarkupCustomLabelId,
    resetMarkupCustomLabelId,
    resetMarkupItemId,
    resetMarkupCategoryId,
    resetMarkupContactId,
    resetMarkupType,
    resetSelectedElement,
    replaceActive,
    resetActive,
    Markup as LabelBuilderMarkup
} from '../../views/LabelBuilder/LabelBuilder.slice'
import contactApi, {ContactData} from '../../apis/contact.api'
import itemApi, {ItemData} from '../../apis/item.api'
import customLabelApi, {CustomLabelData} from '../../apis/customLabel.api'
import labelApi, {ReadSystemLabelRequest} from '../../apis/label.api'
import customLabelService from '../../services/customLabel.service'
import contactService from '../../services/contact.service'
import itemService from '../../services/item.service'
import {RootState} from '../../store'
import Heading from '../Heading'
import SearchPicker, {SearchPickerOption} from '../SearchPicker'
import Select from '../Select'
import categoryApi, {CategoryData} from '../../apis/category.api'
import categoryService from '../../services/category.service'
import {RadioGroupItem} from "../RadioGroup";
import storageService from "../../services/storage.service";
import {useUserPlan} from "../../utils/userPlan.util";

interface RouteState {
    customLabelId?: number
    systemLabelId?: number
    labelTarget?: string
    labelType?: string
}

interface Props {
    transformerRef: RefObject<HTMLTransformerElement>
}

interface State {
    contactName: string
    item: string
    items: ItemData[]

    customLabel: string
    customLabels: CustomLabelData[]

    contact: ContactData | null
    contacts: ContactData[]

    categories: CategoryData[]
    category: string

}

type CombinedProps = Props
    & RouteComponentProps<any, any, RouteState>
    & ConnectedProps<typeof connector>

class LabelProperties extends Component<CombinedProps, State> {
    userPlan = useUserPlan();

    initialState: State = {
        contactName: '',
        item: '',
        customLabel: '',
        contact: null,
        contacts: [],
        items: [],
        categories: [],
        customLabels: [],
        category: ''
    }

    constructor(props: CombinedProps) {
        super(props)
        this.state = this.initialState
        this.handleInputChange = this.handleInputChange.bind(this)
        this.handleSearch = this.handleSearch.bind(this)
        this.handleSearchSelect = this.handleSearchSelect.bind(this)
        this.handleSearchPickerBlur = this.handleSearchPickerBlur.bind(this)
    }
    
    clearTransformer = () => {
        const {transformerRef} = this.props
        const transformerNode = transformerRef.current
        if (!transformerNode) return
        transformerNode.nodes([])
    }

    resetFields() {
        this.props.resetMarkupType()
        this.props.resetMarkupContactId()
        this.props.resetMarkupItemId()
        this.props.resetMarkupCategoryId()
        this.setState(this.initialState)
    }

    handleInputChange(name: string, value: string) {
        this.clearTransformer()
        this.props.resetSelectedElement()
        if (name === 'target') {
            this.props.replaceMarkupTarget(value as LabelBuilderMarkup['target'])
            this.resetFields()
        } else if (name === 'type') {
            const type = value as LabelBuilderMarkup['type']
            this.props.replaceMarkupType(type)
        } else if (name === 'contact') {
            this.props.resetMarkupContactId()
            this.props.resetMarkupType()
            this.setState({
                contact: null,
                contactName: value
            })
        } else if (name === 'item') {
            this.props.resetMarkupItemId()
            this.props.resetMarkupType()
            this.setState({item: value})
        } else if (name === 'category') {
            this.props.resetMarkupCategoryId()
            this.props.resetMarkupType
            this.setState({category: value})
        } else if (name === 'customLabel') {
            this.props.resetMarkupCustomLabelId()
            this.props.resetMarkupType()
            this.setState({customLabel: value})
        }
    }

    handleSearch(name: string, term: string) {
        this.clearTransformer()
        this.props.resetSelectedElement()
        if (name === 'contact') {
            contactApi.search(term, contacts => {
                if (!contacts) return
                this.setState({contacts})
            })
        } else if (name === 'item') {
            itemApi.search(term, items => {
                if (!items) return
                this.setState({items})
            })
        } else if (name === 'category') {
            if (term.length === 0) return
            categoryApi.search(term, categories => {
                if (!categories) return
                this.setState({categories})
            })
        } else if (name === 'customLabel') {
            customLabelApi.search(term, customLabels => {
                if (!customLabels) return
                this.setState({customLabels})
            })
        }
    }

    handleSearchSelect(name: string, option: SearchPickerOption) {
        if (name === 'contact') {
            const id = option.id as number
            const contact = find(this.state.contacts, {id})
            this.props.replaceMarkupContactId(id)
            this.setState({contacts: [], contact: contact || null})
        } else if (name === 'item') {
            const id = option.id as number
            this.props.replaceMarkupItemId(id)
            this.setState({items: []})
        } else if (name === 'category') {
            const id = option.id as number
            this.props.replaceMarkupCategoryId(id)
            this.setState({categories: []})
        } else if (name === 'customLabel') {
            const id = option.id as number
            this.props.replaceMarkupCustomLabelId(id)
            this.setState({customLabels: []})
        }
    }

    handleSearchPickerBlur(name: string) {
        if (name === 'contact') {
            this.setState({contacts: []})
        } else if (name === 'item') {
            this.setState({items: []})
        } else if (name === 'category') {
            this.setState({categories: []})
        } else if (name === 'customLabel') {
            this.setState({customLabels: []})
        }
    }

    get allTargetOptions() {
        return [
            {name: 'Global', value: 'site'},
            {name: 'Contact', value: 'contact'},
            {name: 'Item', value: 'item'},
            {name: 'Category', value: 'category'},
            {name: 'Custom label', value: 'customLabel'}
        ]
    }

    targetOptions = this.userPlan.isLabelBuilderUser
        ? this.allTargetOptions.filter(option => option.name === 'Custom label')
        : this.allTargetOptions


    get typeOptions() {
        const typeOptions = {
            default: [
                {name: 'Inventory pallet', value: 'inventoryPallet'},
                {name: 'Inventory case', value: 'inventoryCase'},
                {name: 'Receiving pallet', value: 'receivingPallet'},
                {name: 'Receiving case', value: 'receivingCase'}
            ],
            customer: [
                {name: 'Inventory pallet', value: 'inventoryPallet'},
                {name: 'Inventory case', value: 'inventoryCase'}
            ],
            vendor: [
                {name: 'Receiving pallet', value: 'receivingPallet'},
                {name: 'Receiving case', value: 'receivingCase'}
            ]
        }
        const {contact} = this.state
        const {target} = this.props
        if (target === 'contact' && contact) {
            const {contactableType} = contact
            return typeOptions[contactableType]
        } else {
            return typeOptions.default
        }
    }

    get contactSearchOptions() {
        return contactService.searchOptions(this.state.contacts)
    }

    get itemSearchOptions() {
        return itemService.searchOptions(this.state.items)
    }

    get categorySearchOptions() {
        return categoryService.searchOptions(this.state.categories)
    }

    get customLabelSearchOptions() {
        return customLabelService.searchOptions(this.state.customLabels)
    }

    get contactTargetForm() {
        const {target} = this.props
        return (target === 'contact') &&
            <Fragment>
                <SearchPicker
                    name="contact"
                    value={this.state.contactName}
                    options={this.contactSearchOptions}
                    label={i18n('Contact')}
                    onSearch={this.handleSearch}
                    onChange={this.handleInputChange}
                    onSelect={this.handleSearchSelect}
                    onBlur={this.handleSearchPickerBlur}/>
            </Fragment>
    }

    get itemTargetForm() {
        const {target} = this.props
        return (target === 'item') &&
            <SearchPicker
                name="item"
                value={this.state.item}
                options={this.itemSearchOptions}
                label={i18n('Item')}
                onSearch={this.handleSearch}
                onChange={this.handleInputChange}
                onSelect={this.handleSearchSelect}
                onBlur={this.handleSearchPickerBlur}/>
    }

    get categoryTargetForm() {
        const {target} = this.props
        return (target === 'category') &&
            <SearchPicker
                name="category"
                value={this.state.category}
                options={this.categorySearchOptions}
                label={i18n('Category')}
                onSearch={this.handleSearch}
                onChange={this.handleInputChange}
                onSelect={this.handleSearchSelect}
                onBlur={this.handleSearchPickerBlur}/>
    }

    get customLabelTargetForm() {
        const {target} = this.props
        return (target === 'customLabel') &&
            <SearchPicker
                name="customLabel"
                value={this.state.customLabel}
                options={this.customLabelSearchOptions}
                label={i18n('Custom label')}
                onSearch={this.handleSearch}
                onChange={this.handleInputChange}
                onSelect={this.handleSearchSelect}
                onBlur={this.handleSearchPickerBlur}/>
    }

    get labelType() {
        const {target} = this.props
        const availableTargets = ['site', 'contact', 'item', 'category']
        return (includes(availableTargets, target)) &&
            <Select
                name="type"
                value={this.props.type || ''}
                options={this.typeOptions}
                label={i18n('Label type')}
                onChange={this.handleInputChange}/>
    }

    readCustomLabel = () => {
        const {state} = this.props.location
        if (!state) return
        const {customLabelId, systemLabelId, labelTarget, labelType} = state
        if (customLabelId) {
            customLabelApi.read(customLabelId, customLabel => {
                if (!customLabel) return
                this.setState({customLabel: customLabel.name})
                this.props.replaceMarkupTarget('customLabel')
                this.props.replaceMarkupCustomLabelId(customLabelId)
            })
        } else if (systemLabelId) {
            this.readSystemLabel(systemLabelId, labelTarget ?? '')
        } else if (!systemLabelId && labelTarget) {
            if (labelTarget == 'site') {
                this.props.replaceMarkupTarget('site')
            } else if (labelTarget == 'contact') {
                this.props.replaceMarkupTarget('contact')
            } else if (labelTarget == 'item') {
                this.props.replaceMarkupTarget('item')
            } else if (labelTarget == 'category') {
                this.props.replaceMarkupTarget('category')
            }
        }
    }

    readSystemLabel = (systemLabelId: number, lableableType: string) => {
        const parts: string[] = lableableType.split('-') // Splitting by comma
        const payload: ReadSystemLabelRequest = {id: systemLabelId, labelableType: parts[0]}
        labelApi.readSystemLabelDetail(payload, label => {
            if (!label) return
            this.props.replaceMarkup(label.markup)
            const labelableId = label.labelableId

            if (parts[0] == 'site') {
                this.props.replaceMarkupTarget('site')
                this.doMarkupType(parts[1])
            } else if (parts[0] == 'contact') {
                this.props.replaceMarkupTarget('contact')

                contactApi.search('', contacts => {
                    if (!contacts) return
                    let c = null
                    for (const value of contacts) {
                        if (value.id = labelableId) {
                            c = value
                            break
                        }
                    }
                    const contactName = label.contactName!
                    this.props.replaceMarkupContactId(label.labelableId)
                    console.log('Setting contact: ' + JSON.stringify(c) + ' and contactName = ' + contactName)
                    this.setState({contacts: [], contact: c as ContactData, contactName})
                })

                this.doMarkupType(parts[1])
            } else if (parts[0] == 'item') {
                this.props.replaceMarkupTarget('item')

                itemApi.search('', items => {
                    if (!items) return
                    this.setState({items})

                    let i = null
                    for (const value of items) {
                        if (value.id == labelableId) {
                            i = value
                            break
                        }
                    }
                    this.setState({items: [], item: i!.code})
                })

                this.doMarkupType(parts[1])
            } else if (parts[0] == 'category') {
                this.props.replaceMarkupTarget('category')

                categoryApi.list(categories => {
                    if (!categories) return
                    this.setState({categories})

                    let i = null
                    for (const value of categories) {
                        if (value.id == labelableId) {
                            i = value
                            break
                        }
                    }
                    this.setState({categories: [], category: i!.name})
                })

                this.doMarkupType(parts[1])
            }
        })
    }

    doMarkupType(t: string) {
        if (t == 'customLabel') {
            this.props.replaceMarkupType('customLabel')
        } else if (t == 'inventoryPallet') {
            this.props.replaceMarkupType('inventoryPallet')
        } else if (t == 'inventoryCase') {
            this.props.replaceMarkupType('inventoryCase')
        } else if (t == 'receivingPallet') {
            this.props.replaceMarkupType('receivingPallet')
        } else if (t == 'receivingCase') {
            this.props.replaceMarkupType('receivingCase')
        }
    }

    componentDidMount() {
        this.readCustomLabel()
    }

    render() {
        return (
            <div>
                <Heading
                    className="mb-2"
                    text={i18n('Label')}
                    size="small"/>
                <Select
                    name="target"
                    value={this.props.target}
                    options={this.targetOptions}
                    label={i18n('Label target')}
                    onChange={this.handleInputChange}/>
                {this.categoryTargetForm}
                {this.contactTargetForm}
                {this.itemTargetForm}
                {this.customLabelTargetForm}
                {this.labelType}
            </div>
        )
    }
}

function mapStateTopProps(state: RootState, props: Props) {
    const {labelBuilder} = produce(state, draft => draft)
    const {markup} = labelBuilder
    const {target, type, contactId, itemId, categoryId} = markup
    return {
        markup,
        target,
        type,
        contactId,
        itemId,
        categoryId
    }
}

const mapDispatchToProps = {
    replaceId,
    resetId,
    replaceMarkup,
    replaceMarkupTarget,
    replaceMarkupType,
    replaceMarkupContactId,
    replaceMarkupItemId,
    replaceMarkupCategoryId,
    replaceMarkupCustomLabelId,
    resetMarkupItemId,
    resetMarkupCategoryId,
    resetMarkupContactId,
    resetMarkupCustomLabelId,
    resetMarkupType,
    resetSelectedElement,
    replaceActive,
    resetActive
}

const connector = connect(mapStateTopProps, mapDispatchToProps)
export default connector(withRouter(LabelProperties))
